import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  HostListener,
  ViewChild,
  ElementRef,
  OnDestroy,
  ChangeDetectorRef
} from "@angular/core";

export interface FileFolderMenuItem {
  label: string;
  action?: string; //emits this action if clicked, unless invoke was provided as a function
  invoke?: Function;
  icon?: string;
  menuItems?: FileFolderMenuItem[];
}
export interface FileFolder {
  name: string;
  type: string;
  id: any;
  children?: FileFolder[];
  parentId?: string;
  metadata?: any;
  expanded?: boolean;
  avoidDelete?: boolean;
  avoidEdit?: boolean;
  avoidDrag?: boolean;
  menuItems?: FileFolderMenuItem[];
}
export interface MenuState {
  isOpen: boolean;
  top: string;
  left: string;
  item: FileFolder | null;
}

@Component({
  selector: "app-file-explorer-input",
  templateUrl: "./file-explorer-input.component.html",
  styleUrls: ["./file-explorer-input.component.scss"],
})
export class FileExplorerInputComponent implements OnInit, OnDestroy {
  public filteredItems: FileFolder[] = [];
  @ViewChild("searchInput") searchInput!: ElementRef;
  @Input() canAddFolder: boolean;
  @Input() placeholder = "Search";
  @Input() shouldPersistList: boolean = false;
  @Input() filterBy: string = "name";
  @Input() backgroundColor: string = "background";
  @Input() searchTerm: string = "";
  @Input() showList = false;
  @Input() selected = "";
  @Input() items: FileFolder[] = [];
  @Input() resetFilterOnListChange: boolean = false;
  @Input() root = true;
  @Output() filterChange = new EventEmitter();
  @Output() focusedEvent = new EventEmitter();
  @Output() toggleMenu = new EventEmitter();
  @Output() itemClicked = new EventEmitter<FileFolder>();
  @Output() searchUpdated = new EventEmitter<string>();
  @Output() searchCleared = new EventEmitter();
  @Output() mouseLeave = new EventEmitter();
  @Output() mouseEnter = new EventEmitter();
  @Output() menuItemClicked = new EventEmitter<{
    item: FileFolder;
    menuItem: FileFolderMenuItem;
  }>();
  @Input() menuState: MenuState = {
    isOpen: false,
    top: "0px",
    left: "0px",
    item: null,
  };
  public submenuState: MenuState = {
    isOpen: false,
    top: "0px",
    left: "0px",
    item: null,
  };
  private initialLoad;
  private timeoutId;
  private documentClickListener;

  constructor(private cdr: ChangeDetectorRef) {}

  @HostListener("mouseleave") onMouseLeave() {
    if (this.root) {
      this.mouseLeave.emit();
    }
  }

  @HostListener("mouseenter") onMouseEnter() {
    if (this.root) {
      this.mouseEnter.emit();
    }
  }

  ngOnInit() {
    // close menu if click occurs outside of menu that is open
    if (this.root) {
      this.documentClickListener = (event: MouseEvent) => {
        if (this.menuState.isOpen) {
          const menu = document.querySelector(".file-explorer-menu");
          const dialog = (event.target as Element)?.closest('scaffold-dialog');
          const clickedInside = menu?.contains(event.target as Node) || dialog;
          if (!clickedInside) {
            this.menuState = {
              isOpen: false,
              top: "0px",
              left: "0px",
              item: null,
            };
            this.closeSubmenu();
            this.cdr.detectChanges();
          }
        }
      };
      document.addEventListener("click", this.documentClickListener);
    }

  }

  ngOnDestroy() {
    if(this.root){
      document.removeEventListener("click", this.documentClickListener);
    }
  }

  onItemClick(item: FileFolder): void {
    if (item.type !== "folder" && this.root) {
      this.showList = false;
    }

    this.itemClicked.emit(item);
  }

  clearSearchTerm() {
    this.searchInput.nativeElement.focus();
    this.searchCleared.emit();
  }

  openSubmenu(event: MouseEvent, menuItem: any) {
    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.submenuState = {
      isOpen: true,
      top: `${rect.top}px`,
      left: `${rect.right}px`,
      item: menuItem,
    };
  }

  handleToggleMenu(event: MouseEvent, item: any) {
    event.preventDefault();
    event.stopImmediatePropagation();

    // need to emit to the root parent to handle the state of it children items
    if (!this.root) {
      return this.toggleMenu.emit({ event, item });
    }

    // if clicking the same item that has an open menu, just close it
    if (this.menuState.item === item && this.menuState.isOpen) {
      this.menuState = {
        isOpen: false,
        top: "0px",
        left: "0px",
        item: null,
      };
      this.closeSubmenu();
      return;
    }

    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    const scrollPos = {
      top: (window.scrollY || document.documentElement.scrollTop) - 10,
      left: (window.scrollX || document.documentElement.scrollLeft) + 2,
    };

    this.menuState = {
      isOpen: true,
      top: `${rect.bottom + scrollPos.top}px`,
      left: `${rect.left + scrollPos.left}px`,
      item: item,
    };
  }

  closeSubmenu() {
    this.submenuState = {
      isOpen: false,
      top: "0px",
      left: "0px",
      item: null,
    };
  }

  handleMenuItemClicked($event, menuItem: FileFolderMenuItem) {
    $event.preventDefault();
    $event.stopPropagation();

    // call function if provided
    if (menuItem.invoke) {
      menuItem.invoke(menuItem);
    }
    // if provided action, emit it
    if (menuItem.action) {
      this.menuItemClicked.emit({ item: this.menuState.item, menuItem });
    }

    // close menu states
    this.menuState = {
      isOpen: false,
      top: "0px",
      left: "0px",
      item: null,
    };
    this.closeSubmenu();
  }

  focusEvent() {
    this.focusedEvent.emit();
  }
}
